import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Shield, CheckCircle2, XCircle, ArrowLeft, ExternalLink, ShieldAlert, Cpu, Wrench, Thermometer, Zap, TrendingDown, Package, Circle, Award, Trophy } from "lucide-react";
import { DEMO_PASSPORT } from "../lib/demoPassport";
import { TrustSparkline } from "../components/TrustSparkline";
import { ResponsiveContainer, AreaChart, Area, YAxis, Tooltip } from "recharts";
import { LvHistoryLog } from "../components/app/LvHistoryLog";
import { is16vModel } from "../components/app/LowVoltageCard";
import { PassportForest } from "../components/app/MyForest";
import Seo from "../components/Seo";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** Buyer flow — used-Tesla shoppers buy the certified report for this VIN. */
const BuyReportCard = ({ passportId, vin }) => {
  const [phase, setPhase] = useState("idle"); // idle | email | waiting | paid
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [purchaseId, setPurchaseId] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    if (phase !== "waiting" || !purchaseId) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await fetch(`${API_BASE}/api/report/status/${purchaseId}`);
        if (!r.ok) return;
        const j = await r.json();
        if (j.paid) {
          setDownloadUrl(j.download_url);
          setPhase("paid");
        }
      } catch { /* keep polling */ }
    }, 3500);
    return () => clearInterval(t);
  }, [phase, purchaseId]);

  const startCheckout = async (e) => {
    e.preventDefault();
    setError("");
    if (!/.+@.+\..+/.test(email)) { setError("Enter a valid email address."); return; }
    setBusy(true);
    const w = window.open("about:blank", "_blank");
    try {
      const r = await fetch(`${API_BASE}/api/report/buy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), passport_id: passportId }),
      });
      if (!r.ok) throw new Error("Could not start checkout — try again.");
      const j = await r.json();
      if (w) w.location = j.checkout_url; else window.open(j.checkout_url, "_blank", "noopener");
      setPurchaseId(j.purchase_id);
      setPhase("waiting");
    } catch (e2) {
      if (w) w.close();
      setError(e2.message || "Could not start checkout — try again.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="rounded-xl border p-4 mb-4"
      style={{ borderColor: "rgba(57,255,20,0.25)", background: "rgba(57,255,20,0.04)" }}
      data-testid="buy-report-card"
    >
      <div className="font-mono text-[10px] uppercase tracking-wider mb-1" style={{ color: "#39FF14" }}>
        Buying this car?
      </div>
      {phase === "paid" ? (
        <div data-testid="buy-report-paid">
          <div className="text-white text-sm font-semibold mb-1">Report unlocked.</div>
          <p className="text-zinc-400 text-xs leading-relaxed mb-3">
            The certified PDF was emailed to <span className="text-white">{email}</span> — and you can download it right now:
          </p>
          <a
            href={downloadUrl}
            data-testid="buy-report-download"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold text-black"
            style={{ background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}
          >
            Download certified report
          </a>
        </div>
      ) : phase === "waiting" ? (
        <div data-testid="buy-report-waiting">
          <div className="text-white text-sm font-semibold mb-1">Complete payment in the Stripe tab.</div>
          <p className="text-zinc-400 text-xs leading-relaxed">
            The moment your payment confirms, the certified PDF is emailed to you and a download
            button appears here automatically.
          </p>
          <div className="mt-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#00E5FF" }} />
            Watching for payment…
          </div>
        </div>
      ) : phase === "email" ? (
        <form onSubmit={startCheckout} data-testid="buy-report-form">
          <p className="text-zinc-400 text-xs leading-relaxed mb-2">
            Where should we send the certified PDF for VIN ····{String(vin || "").slice(-4)}?
          </p>
          <div className="flex gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="buy-report-email"
              disabled={busy}
              className="flex-1 rounded-lg border border-white/10 bg-black/60 px-3 py-2 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]/50"
            />
            <button
              type="submit"
              disabled={busy || !email}
              data-testid="buy-report-submit"
              className="rounded-full px-4 py-2 text-xs font-bold text-black disabled:opacity-50"
              style={{ background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}
            >
              {busy ? "Opening…" : "Pay on Stripe"}
            </button>
          </div>
          {error && <div className="mt-2 text-[11px] text-[#FF6B57]" data-testid="buy-report-error">{error}</div>}
        </form>
      ) : (
        <div>
          <div className="text-white text-sm font-semibold mb-1">
            Get the full certified battery report — $19.99
          </div>
          <p className="text-zinc-400 text-xs leading-relaxed mb-3">
            The battery is this car's most expensive component. Own the cryptographically signed,
            2-page EVIQ Certified Report for this exact VIN — what CarFax can't tell you.
          </p>
          <button
            type="button"
            onClick={() => setPhase("email")}
            data-testid="buy-report-cta"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold text-black"
            style={{ background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}
          >
            Buy certified report
          </button>
        </div>
      )}
    </div>
  );
};

/** Owner's photo of the car — silently hidden when none was uploaded. */
const PassportVehiclePhoto = ({ vin }) => {
  const [ok, setOk] = useState(false);
  if (!vin || !API_BASE) return null;
  return (
    <div
      className={ok ? "rounded-2xl overflow-hidden border border-white/10 mb-4 relative" : "hidden"}
      data-testid="passport-vehicle-photo"
    >
      <img
        src={`${API_BASE}/api/vehicle/photo/${encodeURIComponent(vin)}`}
        alt="Owner-submitted vehicle"
        className="w-full h-48 object-cover"
        onLoad={() => setOk(true)}
        onError={() => setOk(false)}
      />
      <div
        className="absolute bottom-0 inset-x-0 px-3 py-1.5 font-mono text-[8px] uppercase tracking-[0.18em] text-zinc-300"
        style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.7))" }}
      >
        Owner-submitted photo
      </div>
    </div>
  );
};

/** Measured SoH history — the vehicle's real daily-recorded SoH curve,
 *  rendered on the public passport once 2+ snapshots exist. Hidden
 *  silently otherwise (new packs simply have no history yet). */
const PassportSohHistory = ({ vin }) => {
  const [points, setPoints] = useState(null);

  useEffect(() => {
    if (!vin || !API_BASE) return;
    fetch(`${API_BASE}/api/soh/history/${encodeURIComponent(vin)}?days=365`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((j) => setPoints(j.points || []))
      .catch(() => setPoints([]));
  }, [vin]);

  const data = (points || []).map((p) => ({ date: p.date?.slice(5), soh: p.soh_pct }));
  if (data.length < 2) return null;
  const delta = +(data[data.length - 1].soh - data[0].soh).toFixed(2);

  return (
    <>
      <SectionHeader eyebrow="Measured history" title="Real SoH curve — recorded daily." />
      <div
        className="rounded-xl border p-4 mb-6"
        style={{ borderColor: "rgba(0,229,255,0.25)", background: "rgba(0,229,255,0.04)" }}
        data-testid="passport-soh-history"
      >
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-[13px] text-white font-semibold" data-testid="passport-soh-history-delta">
            {delta > 0 ? "+" : ""}{delta}% over {data.length} recorded days
          </div>
          <span className="font-mono text-[8px] uppercase tracking-[0.14em] rounded-full px-1.5 py-0.5 border border-[#00E5FF]/50 text-[#00E5FF] bg-[#00E5FF]/[0.10]">
            Measured
          </span>
        </div>
        <div className="h-24" data-testid="passport-soh-history-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
              <defs>
                <linearGradient id="passportSohFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <YAxis domain={["dataMin - 0.3", "dataMax + 0.3"]} hide />
              <Tooltip
                contentStyle={{ background: "#0a0f0a", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, fontSize: 11 }}
                labelStyle={{ color: "#888", fontFamily: "monospace", fontSize: 10 }}
                formatter={(v) => [`${v}% SoH`, null]}
              />
              <Area type="monotone" dataKey="soh" stroke="#00E5FF" strokeWidth={2} fill="url(#passportSohFill)" dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="font-mono text-[9px] text-zinc-600 mt-2 leading-relaxed">
          One snapshot stored automatically each day the owner opens EVIQ — not modeled, not extrapolated.
        </p>
      </div>
    </>
  );
};

/**
 * BatteryPassportPage — publicly viewable render of a stored
 * EVIQ Battery Passport. Renders exactly the snapshot saved at creation
 * time (no live re-computation), which is what makes it tamper-evident.
 *
 * URL: /passport/:id
 *
 * Query params:
 *   ?sig=<hmac>  — optional HMAC-SHA256 signature from the share URL.
 *                  When present the page auto-verifies against
 *                  /api/battery/verify and renders a "Verified" or
 *                  "Signature mismatch" badge.
 *
 * Special-case: /passport/demo renders the inline demo snapshot with an
 * "EVIQ - EXAMPLE" watermark so marketing visitors can preview the
 * artifact without needing a real connected vehicle.
 */
export default function BatteryPassportPage() {
  const params = useParams();
  // Route may be /passport/:id OR /p/:slug — resolve the two into a
  // single "how to fetch this passport" strategy.
  const routeSlug = params.slug || null;
  const routeId = params.id || null;
  const [search] = useSearchParams();
  const sig = search.get("sig") || "";
  const isDemo = routeId === "demo";
  const [state, setState] = useState(
    isDemo
      ? { loading: false, error: null, rec: DEMO_PASSPORT }
      : { loading: true, error: null, rec: null }
  );
  const [verify, setVerify] = useState(null); // { valid, signed, ... }
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  useEffect(() => {
    if (isDemo || !API_BASE) return;
    if (!routeId && !routeSlug) return;
    const url = routeSlug
      ? `${API_BASE}/api/battery/passport/slug/${encodeURIComponent(routeSlug)}`
      : `${API_BASE}/api/battery/passport/${routeId}`;
    fetch(url)
      .then(async (r) => {
        if (r.status === 404) throw new Error("This passport was not found. It may be a typo in the link.");
        if (r.status === 410) throw new Error("This passport has expired. Ask the owner to generate a fresh one.");
        if (!r.ok) throw new Error("Something went wrong loading this passport.");
        return r.json();
      })
      .then((rec) => setState({ loading: false, error: null, rec }))
      .catch((e) => setState({ loading: false, error: e.message, rec: null }));
  }, [routeId, routeSlug, isDemo]);

  // Auto-verify signature when the passport is loaded on a real page.
  useEffect(() => {
    if (isDemo || !state.rec || !API_BASE) return;
    const pid = state.rec.passport_id;
    if (!pid) return;
    const url = sig
      ? `${API_BASE}/api/battery/verify/${pid}?sig=${encodeURIComponent(sig)}`
      : `${API_BASE}/api/battery/verify/${pid}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => v && setVerify(v))
      .catch(() => {});
  }, [sig, state.rec, isDemo]);

  if (state.loading) return <FullScreenMsg msg="Loading passport…" testid="passport-loading" />;
  if (state.error)
    return (
      <FullScreenMsg
        error
        msg={state.error}
        testid="passport-error"
      />
    );

  const s = state.rec.snapshot;
  const bandColor = (s.risk_band === "Low" ? "#39FF14" : s.risk_band === "Moderate" ? "#FFB020" : "#FF6B57");

  // Per-passport SEO (title/description) — makes each shared link rich
  // in preview cards and unique on Google.
  const vinSuffix = state.rec.vin ? state.rec.vin.slice(-6) : "";
  const seoTitle = isDemo
    ? "Demo Digital Battery Passport — see what buyers get"
    : `${s.model_year || ""} ${s.model || "Tesla"}${s.trim ? ` ${s.trim}` : ""} — Battery Passport · VIN …${vinSuffix}`.trim();
  const seoDesc = isDemo
    ? "See an example EVIQ Digital Battery Passport — the cryptographically-signed, buyer-verifiable battery report that lifts used-Tesla resale value."
    : `Verified EVIQ Digital Battery Passport for a ${s.model_year || ""} ${s.model || "Tesla"} — EVIQ Estimated SoH ${s.soh_percent ?? "?"}%, Trust Score ${s.trust_score ?? "?"}, warranty ${s.warranty_status || "tracked"}. Cryptographically signed.`;
  const seoCanonical = routeSlug
    ? `https://eviq.pro/p/${routeSlug}`
    : `https://eviq.pro/passport/${routeId}`;

  return (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ background: "#050505" }} data-testid="passport-page">
      <Seo
        title={seoTitle}
        description={seoDesc}
        canonical={seoCanonical}
      />
      {/* EXAMPLE watermark — only rendered for /passport/demo */}
      {isDemo && (
        <div
          aria-hidden="true"
          data-testid="passport-demo-watermark"
          className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-hidden"
        >
          <div
            className="font-display font-black whitespace-nowrap select-none"
            style={{
              fontSize: "clamp(72px, 18vw, 220px)",
              transform: "rotate(-24deg)",
              letterSpacing: "0.08em",
              color: "rgba(57,255,20,0.06)",
              textShadow: "0 0 40px rgba(57,255,20,0.08)",
            }}
          >
            EVIQ · EXAMPLE
          </div>
        </div>
      )}
      <div className="max-w-2xl mx-auto px-5 py-8 relative z-10">
        {isDemo && (
          <div
            data-testid="passport-demo-banner"
            className="rounded-xl border px-4 py-2.5 mb-4 flex items-center gap-2"
            style={{ borderColor: "rgba(255,176,32,0.4)", background: "rgba(255,176,32,0.08)" }}
          >
            <div
              className="font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-1 rounded"
              style={{ background: "rgba(255,176,32,0.18)", color: "#FFB020" }}
            >
              Example
            </div>
            <div className="text-[12px] text-zinc-200 leading-snug">
              This is a <span className="text-white font-semibold">demonstration</span> Battery Passport — not tied to a real vehicle.
              Connect your Tesla to generate your own verified passport.
            </div>
          </div>
        )}
        {/* Header */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-zinc-500 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={2.4} />
          EVIQ Battery Intelligence
        </Link>

        {/* Hero */}
        <div className="flex items-center gap-3 mb-4">
          <img
            src="/eviq_shield_transparent.png"
            alt="EVIQ Digital Battery Passport shield"
            data-testid="passport-hero-shield"
            className="w-16 h-16 shrink-0"
            style={{
              objectFit: "contain",
              filter: "drop-shadow(0 0 14px rgba(57,255,20,0.35))",
            }}
          />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              EVIQ Certified Battery Report™
            </div>
            <h1 className="font-display text-2xl font-semibold leading-tight" data-testid="passport-title">
              {s.year} {s.model}
              {s.trim ? ` · ${s.trim}` : ""}
            </h1>
            <div className="font-mono text-[10px] text-zinc-500 mt-1">
              The complete battery history and verification report.
            </div>
          </div>
        </div>

        {/* Trust / Grade / Verify — top row */}
        <TrustGradeStrip snapshot={s} verify={verify} passportId={state.rec.passport_id} createdAt={state.rec.created_at} expiresAt={state.rec.expires_at} />

        {/* Owner-submitted vehicle photo (renders only when one exists) */}
        <PassportVehiclePhoto vin={s.vin} />

        {/* EVIQ Trust Score™ + Health Grade™ + core capacity card */}
        <TrustHeroCard snapshot={s} onOpenBreakdown={() => setBreakdownOpen(true)} vin={s.vin} />
        <TrustScoreBreakdownModal
          open={breakdownOpen}
          onClose={() => setBreakdownOpen(false)}
          trustScore={s.trust_score}
          breakdown={s.trust_score_breakdown}
          grade={s.health_grade}
        />

        {/* Top-2 actionable tips for low-grade packs (trust < 70) */}
        <GradeImprovementTips snapshot={s} />

        {/* Trees planted through this battery — Earth Initiative social proof */}
        {!isDemo && s.vin && !String(s.vin).includes("•") && (
          <PassportForest vin={String(s.vin).toUpperCase()} />
        )}

        {/* Signature primary vitals (Battery Health, Remaining Capacity,
            Estimated Remaining Life, EVIQ Score) */}
        <VitalsGrid snapshot={s} />

        {/* Headline metrics */}
        <SectionHeader eyebrow="Battery signature" title="Snapshot at time of issue" />
        <div className="grid grid-cols-2 gap-3 mb-6">
          <MetricCard label="EVIQ Estimated SoH" value={`${s.soh_pct}%`} big color="#39FF14" testid="passport-soh" />
          <MetricCard label="Battery risk" value={`${s.risk_score || "—"}%`} big color={bandColor} sub={s.risk_band || ""} testid="passport-risk" />
          <MetricCard label="Degradation velocity" value={`${Math.abs(s.delta_30d_pct).toFixed(2)}%/mo`} color="#00E5FF" />
          <MetricCard label="Warranty floor" value={`${s.warranty_threshold_pct}%`} color="#FFB020" sub={`${s.warranty_years}y / ${(s.warranty_miles / 1000).toFixed(0)}k mi`} />
          <MetricCard label="EPA (when new)" value={`${s.epa_when_new_mi} mi`} color="#7FD1FF" />
          <MetricCard label="Pack capacity" value={`${s.pack_kwh} kWh`} color="#7FD1FF" />
        </div>

        {/* Measured SoH curve — real daily snapshots for this VIN */}
        {!isDemo && s.vin && !String(s.vin).includes("•") && (
          <PassportSohHistory vin={String(s.vin).toUpperCase()} />
        )}

        {/* DNA strands */}
        {(s.thermal_stress_index || s.charging_quality_score || s.forecast_3yr_soh) && (
          <>
            <SectionHeader eyebrow="Battery DNA strands" title="Deeper diagnostics" />
            <div className="grid grid-cols-2 gap-3 mb-6">
              {s.thermal_stress_index != null && <MetricCard label="Thermal stress" value={`${s.thermal_stress_index}/100`} color="#FFB020" />}
              {s.charging_quality_score != null && <MetricCard label="Charging quality" value={`${s.charging_quality_score}/100`} color="#39FF14" />}
              {s.calibration_confidence_pct != null && <MetricCard label="Calibration" value={`${s.calibration_confidence_pct}%`} color="#00E5FF" />}
              {s.fleet_rank_pct != null && <MetricCard label="Fleet rank" value={`Top ${100 - s.fleet_rank_pct}%`} color="#00E5FF" />}
              {s.forecast_3yr_soh != null && <MetricCard label="Projected 3-yr SoH" value={`${s.forecast_3yr_soh}%`} color="#39FF14" />}
              {s.forecast_8yr_soh != null && <MetricCard label="Projected 8-yr SoH" value={`${s.forecast_8yr_soh}%`} color="#FFB020" />}
              {s.battery_value_usd != null && <MetricCard label="Battery value" value={`$${s.battery_value_usd.toLocaleString()}`} color="#39FF14" />}
            </div>
          </>
        )}

        {/* Usage + care habits */}
        <SectionHeader eyebrow="Care & usage" title="How this pack has been treated." />
        <div className="grid grid-cols-2 gap-3 mb-6" data-testid="passport-care-grid">
          {s.fast_charge_pct != null && <MetricCard label="Fast charging" value={`${s.fast_charge_pct}%`} color="#FFB020" testid="passport-fast-charge" />}
          {s.ac_charge_pct != null && <MetricCard label="AC charging" value={`${s.ac_charge_pct}%`} color="#39FF14" testid="passport-ac-charge" />}
          {s.battery_cycles != null && <MetricCard label="Battery cycles" value={s.battery_cycles.toLocaleString()} color="#7FD1FF" testid="passport-cycles" />}
          {s.avg_soc_pct != null && <MetricCard label="Average SoC" value={`${s.avg_soc_pct}%`} color="#00E5FF" testid="passport-avg-soc" />}
          {s.avg_daily_miles != null && <MetricCard label="Average daily usage" value={`${s.avg_daily_miles} mi`} color="#00E5FF" testid="passport-daily-miles" />}
          {s.heat_exposure && <MetricCard label="Extreme heat exposure" value={s.heat_exposure} color={exposureTone(s.heat_exposure)} testid="passport-heat" />}
          {s.cold_exposure && <MetricCard label="Extreme cold exposure" value={s.cold_exposure} color={exposureTone(s.cold_exposure)} testid="passport-cold" />}
          {s.estimated_remaining_life_years != null && <MetricCard label="Estimated remaining life" value={`${s.estimated_remaining_life_years} yrs`} color="#39FF14" testid="passport-remaining-life" />}
        </div>

        {/* Verification & warranty facts */}
        <SectionHeader eyebrow="Verification facts" title="Warranty · history · software." />
        <div className="grid grid-cols-2 gap-3 mb-6" data-testid="passport-facts-grid">
          <FactCard label="Warranty status" value="Active" tone="green" testid="passport-warranty-status" />
          <FactCard label="Current software" value={s.current_software_verified === false ? "Unverified" : "Verified"} tone={s.current_software_verified === false ? "amber" : "green"} sub={s.current_software_version || null} testid="passport-software" />
          <FactCard label="Rapid degradation" value={s.rapid_degradation_detected ? "Detected" : "No"} tone={s.rapid_degradation_detected ? "red" : "green"} testid="passport-rapid-degradation" />
          <FactCard label="Battery repaired" value={s.battery_repaired ? "Yes" : "No"} tone={s.battery_repaired ? "amber" : "green"} sub={s.battery_repaired_date || null} testid="passport-repaired" />
          <FactCard label="Battery replaced" value={s.battery_replaced ? "Yes" : "No"} tone={s.battery_replaced ? "cyan" : "green"} sub={s.battery_replaced_date || null} testid="passport-replaced" />
          {s.predicted_range_loss_3yr_pct != null && (
            <FactCard label="Predicted range loss · 3 yr" value={`${s.predicted_range_loss_3yr_pct}%`} tone="cyan" testid="passport-range-loss-3yr" />
          )}
        </div>

        {/* EVIQ Battery Legacy™ */}
        {Array.isArray(s.legacy_timeline) && s.legacy_timeline.length > 0 && (
          <BatteryLegacySection timeline={s.legacy_timeline} />
        )}

        {/* Share to leaderboard opt-in — only when we have a real signature */}
        {!isDemo && state.rec?.signature && (
          <LeaderboardOptIn passportId={state.rec.passport_id} signature={state.rec.signature} />
        )}

        {/* Buyer flow — one-off certified report purchase for shoppers */}
        {!isDemo && state.rec?.passport_id && (
          <BuyReportCard passportId={state.rec.passport_id} vin={s.vin} />
        )}

        {/* Vanity URL claim — owner-only panel. We treat "URL contained a
            valid sig" as the ownership proof, matching the same threshold
            we use for leaderboard opt-in. */}
        {!isDemo && state.rec?.signature && verify?.valid && sig && (
          <VanityUrlPanel
            passportId={state.rec.passport_id}
            signature={state.rec.signature}
            currentSlug={state.rec.snapshot_slug || null}
            onClaimed={(slug) => setState((prev) => ({
              ...prev,
              rec: { ...prev.rec, snapshot_slug: slug },
            }))}
          />
        )}

        {/* VIN + methodology */}
        <div
          className="rounded-xl border p-4 mb-4"
          style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
        >
          <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">VIN</div>
          <div className="font-mono text-[13px] text-white">{s.vin}</div>
        </div>

        {/* 12V service history — owner-logged replacement timeline. Renders
            nothing when the VIN has no entries. */}
        {s.vin && !String(s.vin).includes("•") && (
          <LvHistoryLog
            vin={String(s.vin).toUpperCase()}
            framed
            liIonLabel={is16vModel(s.model) ? "Lithium-ion 16V" : "Lithium-ion 12V"}
          />
        )}

        <div
          className="rounded-xl border p-4 mb-6"
          style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
        >
          <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Methodology</div>
          <p className="text-[12px] text-zinc-300 leading-relaxed">
            SoH is derived by scaling the vehicle's live <code className="font-mono">battery_range</code> reading to 100% state-of-charge and dividing by the EPA-rated range for the exact trim (per Tesla's own <code className="font-mono">vehicle_config</code>). Warranty thresholds come from Tesla's published warranty terms. EVIQ never claims warranty qualification — a professional Tesla Service diagnostic is required for any warranty action.
          </p>
        </div>

        {/* Trust footer */}
        <div className="text-center pt-2 pb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
            EVIQ Battery Intelligence
          </div>
          <a
            href="https://eviq.pro"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-white/70 hover:text-white transition-colors"
          >
            eviq.pro
            <ExternalLink className="w-3 h-3" strokeWidth={2} />
          </a>
        </div>
      </div>
    </div>
  );
}

const SectionHeader = ({ eyebrow, title }) => (
  <div className="mb-3">
    <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">{eyebrow}</div>
    <h2 className="font-display text-lg text-white font-semibold leading-tight mt-0.5">{title}</h2>
  </div>
);

const MetricCard = ({ label, value, sub, color, big, testid }) => (
  <div
    className="rounded-xl border p-4"
    style={{ borderColor: "rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.02)" }}
    data-testid={testid}
  >
    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
    <div
      className={big ? "font-display text-2xl font-semibold leading-none" : "font-display text-lg font-semibold leading-none"}
      style={{ color: color || "#ffffff" }}
    >
      {value}
    </div>
    {sub && <div className="font-mono text-[10px] text-zinc-500 mt-1">{sub}</div>}
  </div>
);

const FullScreenMsg = ({ msg, error, testid }) => (
  <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#050505" }} data-testid={testid}>
    <div className="text-center max-w-md">
      {error ? (
        <XCircle className="w-10 h-10 mx-auto mb-3" style={{ color: "#FF6B57" }} strokeWidth={2} />
      ) : (
        <Shield className="w-10 h-10 mx-auto mb-3 animate-pulse" style={{ color: "#39FF14" }} strokeWidth={2} />
      )}
      <div className="text-white text-sm mb-4">{msg}</div>
      <Link to="/" className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 hover:text-white">
        ← Back to EVIQ
      </Link>
    </div>
  </div>
);

// -------------------------------------------------------------------------
// EVIQ Battery Passport™ v2 sub-components
// -------------------------------------------------------------------------

const TrustGradeStrip = ({ snapshot: s, verify, passportId, createdAt, expiresAt }) => (
  <div
    className="rounded-xl border p-3 flex items-center gap-3 mb-4"
    style={{ borderColor: "rgba(57,255,20,0.35)", background: "rgba(57,255,20,0.06)" }}
    data-testid="passport-verify-strip"
  >
    <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "#39FF14" }} strokeWidth={2.4} />
    <div className="min-w-0 flex-1">
      <div className="text-[13px] font-semibold text-white">
        {verify?.valid === false ? "Signature mismatch — do not trust." :
         verify?.valid ? "Verified — cryptographically signed by EVIQ." :
         "Issued by EVIQ Battery Intelligence."}
      </div>
      <div className="font-mono text-[10px] text-zinc-400 mt-0.5">
        Report ID <span className="text-white">{passportId}</span> · issued {new Date(createdAt).toLocaleDateString()} · expires {new Date(expiresAt).toLocaleDateString()}
      </div>
    </div>
  </div>
);

// Big hero: Trust Score + Health Grade + Battery Health + Remaining Capacity
const gradeContext = (s) => {
  const soh = s.soh_pct;
  const floor = s.warranty_threshold_pct;
  if (soh == null || floor == null) return null;
  const headroom = Math.round((soh - floor) * 10) / 10;
  if (headroom <= 5)
    return `${soh}% SoH is only ${headroom} pts above the ${floor}% warranty floor — bottom of the fleet. A professional diagnostic is recommended before purchase.`;
  if (headroom <= 12)
    return `${soh}% SoH is ${headroom} pts above the ${floor}% warranty floor — below the 85–90% typical for this age.`;
  return `${soh}% SoH sits ${headroom} pts above the ${floor}% warranty floor — healthy headroom.`;
};

const TrustHeroCard = ({ snapshot: s, onOpenBreakdown, vin }) => {
  const trust = s.trust_score;
  const grade = s.health_grade;
  const hasBreakdown = !!s.trust_score_breakdown;
  const tone = trust == null ? "#39FF14"
    : trust >= 90 ? "#39FF14"
    : trust >= 75 ? "#7FD1FF"
    : trust >= 60 ? "#FFB020"
    : "#FF6B57";
  return (
    <div
      className="rounded-2xl border p-5 mb-4 relative"
      style={{
        borderColor: `${tone}55`,
        background: `linear-gradient(135deg, ${tone}18 0%, rgba(255,255,255,0.02) 100%)`,
      }}
      data-testid="passport-trust-hero"
    >
      <div className="flex items-start gap-5 flex-wrap">
        {trust != null && (
          <button
            type="button"
            onClick={hasBreakdown ? onOpenBreakdown : undefined}
            disabled={!hasBreakdown}
            className="text-left group focus:outline-none"
            data-testid="passport-trust-score-tap"
            aria-label={hasBreakdown ? "Tap to see Trust Score breakdown" : undefined}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 inline-flex items-center gap-1.5">
              EVIQ Trust Score™
              {hasBreakdown && (
                <span
                  className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 group-hover:text-white transition-colors border border-white/15 rounded px-1 py-[1px]"
                >
                  breakdown ›
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <div
                className="font-display text-6xl font-semibold leading-none group-hover:opacity-80 transition-opacity"
                style={{ color: tone }}
                data-testid="passport-trust-score"
              >
                {trust}
              </div>
              <div className="font-mono text-[11px] text-zinc-500">/ 100</div>
            </div>
          </button>
        )}
        {grade && (
          <div className="min-w-0 border-l border-white/10 pl-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              EVIQ Health Grade™
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Award className="w-6 h-6" style={{ color: tone }} strokeWidth={2.4} />
              <div
                className="font-display text-5xl font-semibold leading-none"
                style={{ color: tone }}
                data-testid="passport-health-grade"
              >
                {grade}
              </div>
            </div>
          </div>
        )}
        {trust != null && vin && (
          <div className="border-l border-white/10">
            <TrustSparkline vin={vin} currentScore={trust} tone={tone} />
          </div>
        )}
        {s.legendary_badge && (
          <div className="min-w-0 border-l border-white/10 pl-5">
            <div
              className="font-mono text-[10px] uppercase tracking-[0.22em]"
              style={{ color: "#FFB020" }}
              data-testid="passport-legendary-badge"
            >
              Legendary Preservation™
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Trophy className="w-6 h-6" style={{ color: "#FFB020" }} strokeWidth={2.4} />
              <div className="text-[11px] text-zinc-300 leading-snug max-w-[180px]">
                Trust Score {s.legendary_badge.threshold}+ sustained for {s.legendary_badge.days_qualified} days
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="text-[11px] text-zinc-400 leading-relaxed mt-3 max-w-lg">
        Composite of battery health, charging habits, thermal stress, degradation trend
        and repair history. A+ means the pack has been treated exceptionally.
      </div>
      {gradeContext(s) && (
        <div
          className="text-[11px] leading-relaxed mt-2 max-w-lg font-mono"
          style={{ color: tone }}
          data-testid="passport-grade-context"
        >
          {gradeContext(s)}
        </div>
      )}
    </div>
  );
};

// Full-screen modal that visualises the Trust Score point-by-point.
// Every row shows the component's contribution as a stacked horizontal bar
// so a buyer instantly sees WHERE the score came from — 35 possible SoH
// points, 15 velocity, etc. Penalties render in red under the total.
const BREAKDOWN_ROWS = [
  { key: "soh",         label: "Battery health (SoH)",        max: 35, tone: "#39FF14" },
  { key: "velocity",    label: "Degradation velocity",         max: 15, tone: "#39FF14" },
  { key: "charging",    label: "Charging quality",             max: 10, tone: "#7FD1FF" },
  { key: "thermal",     label: "Thermal stress",               max: 10, tone: "#7FD1FF" },
  { key: "software",    label: "Software verified",            max:  5, tone: "#00E5FF" },
  { key: "fleet_rank",  label: "Fleet rank",                   max:  5, tone: "#00E5FF" },
  { key: "warranty",    label: "Warranty headroom",            max:  5, tone: "#00E5FF" },
  { key: "not_rapid",   label: "No rapid degradation flag",    max:  2, tone: "#39FF14" },
];

const TrustScoreBreakdownModal = ({ open, onClose, trustScore, breakdown, grade }) => {
  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);
  if (!open) return null;
  const b = breakdown || {};
  const positiveTotal = BREAKDOWN_ROWS.reduce((s, r) => s + Math.max(0, b[r.key] || 0), 0);
  const repaired = b.repaired_penalty || 0;
  const replaced = b.replaced_bonus || 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-3 pb-3 sm:pb-0"
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="trust-breakdown-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border p-5 max-h-[85vh] overflow-y-auto"
        style={{
          background: "#0a0a0a",
          borderColor: "rgba(255,255,255,0.14)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
        }}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              EVIQ Trust Score™ · breakdown
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="font-display text-4xl font-semibold text-white leading-none">
                {trustScore}
              </span>
              <span className="font-mono text-[11px] text-zinc-500">/ 100</span>
              {grade && (
                <span className="ml-1 font-mono text-[10px] uppercase tracking-wider px-1.5 py-[2px] rounded border border-white/15">
                  Grade {grade}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="trust-breakdown-close"
            aria-label="Close breakdown"
            className="w-8 h-8 rounded-md border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/[0.06] transition-colors"
          >
            <XCircle className="w-4 h-4" strokeWidth={2.2} />
          </button>
        </div>
        <div className="space-y-2.5">
          {BREAKDOWN_ROWS.map((row) => {
            const got = Math.max(0, Math.min(row.max, b[row.key] ?? 0));
            const pct = (got / row.max) * 100;
            return (
              <div key={row.key} data-testid={`trust-breakdown-${row.key}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <div className="text-[12px] text-zinc-200 leading-none">{row.label}</div>
                  <div className="font-mono text-[10px] text-zinc-500">
                    <span className="text-white">{got}</span>
                    <span className="text-zinc-600"> / {row.max}</span>
                  </div>
                </div>
                <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div
                    className="h-full rounded-full transition-[width]"
                    style={{ width: `${pct}%`, background: row.tone }}
                  />
                </div>
              </div>
            );
          })}
          {(repaired < 0 || replaced > 0) && (
            <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5">
              {replaced > 0 && (
                <div className="flex items-baseline justify-between text-[12px]" data-testid="trust-breakdown-replaced">
                  <span className="text-zinc-200">Battery replaced (fresh pack)</span>
                  <span className="font-mono" style={{ color: "#39FF14" }}>+{replaced}</span>
                </div>
              )}
              {repaired < 0 && (
                <div className="flex items-baseline justify-between text-[12px]" data-testid="trust-breakdown-repaired">
                  <span className="text-zinc-200">Battery repaired penalty</span>
                  <span className="font-mono" style={{ color: "#FF6B57" }}>{repaired}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mt-4 pt-3 border-t border-white/5 text-[11px] text-zinc-500 leading-relaxed">
          Total possible: <span className="text-white">100</span> ·
          Awarded on this pack: <span className="text-white">{positiveTotal + replaced + repaired}</span>
        </div>
        <div className="text-[10px] font-mono text-zinc-600 leading-relaxed mt-2">
          The Trust Score is a deterministic composite. Same inputs → same score, every time.
        </div>
      </div>
    </div>
  );
};

// Actionable trust-score components → concrete owner tips. Shown to
// low-grade owners so the F/D isn't a dead end — it's a to-do list.
const ACTIONABLE_MAX = { soh: 35, velocity: 15, charging: 10, thermal: 10, software: 5 };
const TIP_LIBRARY = {
  soh: (gap) => ({
    title: "Recalibrate & preserve capacity",
    desc: `Charge to 100% once a month and let the BMS re-estimate range — State of Health is the biggest factor (${gap} pts on the table).`,
  }),
  velocity: (gap) => ({
    title: "Slow the degradation trend",
    desc: `Keep daily charge between 20–80% and avoid back-to-back DC fast sessions (up to ${gap} pts).`,
  }),
  charging: (gap) => ({
    title: "Shift to Level 2 home charging",
    desc: `A lower fast-charge share directly lifts charging quality (up to ${gap} pts).`,
  }),
  thermal: (gap) => ({
    title: "Cut heat exposure",
    desc: `Garage or shade parking during heat waves lowers thermal stress (up to ${gap} pts).`,
  }),
  software: (gap) => ({
    title: "Install the latest software update",
    desc: `A verified current build is worth ${gap} pts instantly.`,
  }),
};

const GradeImprovementTips = ({ snapshot: s }) => {
  const b = s.trust_score_breakdown;
  if (!b || s.trust_score == null || s.trust_score >= 70) return null;
  const gaps = Object.entries(ACTIONABLE_MAX)
    .map(([k, max]) => ({ k, gap: Math.max(0, max - Math.max(0, Math.min(max, b[k] ?? 0))) }))
    .filter((g) => g.gap >= 2)
    .sort((a, z) => z.gap - a.gap)
    .slice(0, 2);
  if (!gaps.length) return null;
  return (
    <div
      className="rounded-xl border p-4 mb-4"
      style={{ borderColor: "rgba(255,176,32,0.35)", background: "rgba(255,176,32,0.05)" }}
      data-testid="grade-improvement-tips"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: "#FFB020" }}>
        Fastest ways to raise this score
      </div>
      <div className="space-y-2.5">
        {gaps.map(({ k, gap }, i) => {
          const tip = TIP_LIBRARY[k](gap);
          return (
            <div key={k} className="flex items-start gap-2.5" data-testid={`grade-tip-${k}`}>
              <span
                className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center font-mono text-[10px] font-bold"
                style={{ background: "rgba(255,176,32,0.15)", color: "#FFB020", border: "1px solid rgba(255,176,32,0.4)" }}
              >
                {i + 1}
              </span>
              <div>
                <div className="text-[13px] text-white font-semibold leading-tight">{tip.title}</div>
                <div className="text-[11px] text-zinc-400 leading-snug mt-0.5">{tip.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const VitalsGrid = ({ snapshot: s }) => (
  <div className="grid grid-cols-2 gap-3 mb-6" data-testid="passport-vitals">
    <MetricCard label="EVIQ Estimated SoH" value={`${Math.round(s.soh_pct)}%`} big color="#39FF14" testid="passport-battery-health" />
    <MetricCard label="Remaining Capacity" value={`${(s.remaining_capacity_pct ?? s.soh_pct).toFixed(1)}%`} big color="#7FD1FF" testid="passport-remaining-capacity" />
    {s.eviq_score != null && (
      <MetricCard label="EVIQ Score" value={`${s.eviq_score}/100`} color="#00E5FF" sub={s.eviq_score_grade || null} testid="passport-eviq-score-mini" />
    )}
    <MetricCard label="Degradation" value={`${Math.abs(s.delta_30d_pct).toFixed(2)}%/mo`} color="#FFB020" testid="passport-degradation" />
  </div>
);

const exposureTone = (label) => {
  if (label === "High") return "#FF6B57";
  if (label === "Moderate") return "#FFB020";
  if (label === "Low") return "#7FD1FF";
  return "#39FF14"; // Very Low or default
};

const FactCard = ({ label, value, sub, tone = "green", testid }) => {
  const color = tone === "green" ? "#39FF14"
    : tone === "amber" ? "#FFB020"
    : tone === "red" ? "#FF6B57"
    : "#7FD1FF";
  return (
    <div
      className="rounded-xl border p-3"
      style={{ borderColor: `${color}44`, background: `${color}0A` }}
      data-testid={testid}
    >
      <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className="font-display text-lg font-semibold leading-none" style={{ color }}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
};

const kindIcon = {
  firmware: Cpu,
  service: Wrench,
  climate: Thermometer,
  charging: Zap,
  trend: TrendingDown,
  milestone: Package,
};

const toneColor = {
  green: "#39FF14",
  cyan: "#00E5FF",
  amber: "#FFB020",
  red: "#FF6B57",
};

const LeaderboardOptIn = ({ passportId, signature }) => {
  const [optIn, setOptIn] = useState(false);
  const [stateCode, setStateCode] = useState("");
  const [status, setStatus] = useState("idle");
  const submit = async () => {
    setStatus("saving");
    try {
      const r = await fetch(`${API_BASE}/api/leaderboard/optin/${encodeURIComponent(passportId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ opt_in: optIn, state: stateCode || null, signature }),
      });
      setStatus(r.ok ? "done" : "error");
    } catch {
      setStatus("error");
    }
  };
  return (
    <div
      className="rounded-xl border p-4 mb-6"
      style={{ borderColor: "rgba(255,176,32,0.30)", background: "rgba(255,176,32,0.04)" }}
      data-testid="passport-leaderboard-optin"
    >
      <div className="flex items-center gap-2 mb-2">
        <Award className="w-4 h-4" style={{ color: "#FFB020" }} strokeWidth={2.4} />
        <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "#FFB020" }}>
          Share to Leaderboard
        </div>
      </div>
      <div className="text-[12px] text-zinc-300 leading-relaxed mb-2">
        Rank this pack on the public "top-preserved packs" board. Only the VIN
        suffix, model, year and score are shared — no personal data.
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-1.5 text-[12px] text-white cursor-pointer">
          <input
            type="checkbox"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            data-testid="passport-leaderboard-optin-checkbox"
            className="w-3.5 h-3.5 accent-[#FFB020]"
          />
          Include on leaderboard
        </label>
        <input
          type="text"
          maxLength={2}
          value={stateCode}
          onChange={(e) => setStateCode(e.target.value.toUpperCase())}
          placeholder="ST"
          data-testid="passport-leaderboard-optin-state"
          className="w-14 rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[11px] font-mono text-white placeholder-zinc-600 uppercase focus:outline-none focus:border-[#FFB020]/60"
        />
        <button
          type="button"
          onClick={submit}
          disabled={status === "saving"}
          data-testid="passport-leaderboard-optin-submit"
          className="rounded-md brand-gradient text-black px-3 py-1 text-[11px] font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {status === "saving" ? "Saving…" : status === "done" ? "Saved" : "Save"}
        </button>
      </div>
      {status === "done" && (
        <div className="text-[10px] text-[#39FF14] mt-2" data-testid="passport-leaderboard-optin-success">
          Preference saved.
        </div>
      )}
      {status === "error" && (
        <div className="text-[10px] text-[#FF6B57] mt-2">
          Couldn't save — signature mismatch or network error.
        </div>
      )}
    </div>
  );
};


const BatteryLegacySection = ({ timeline }) => {
  const [filter, setFilter] = useState("all");
  const counts = timeline.reduce((acc, e) => {
    acc[e.kind] = (acc[e.kind] || 0) + 1;
    return acc;
  }, {});
  const kinds = [
    { key: "all",       label: "All",       icon: Circle },
    { key: "firmware",  label: "Firmware",  icon: Cpu },
    { key: "service",   label: "Service",   icon: Wrench },
    { key: "charging",  label: "Charging",  icon: Zap },
    { key: "climate",   label: "Climate",   icon: Thermometer },
    { key: "trend",     label: "Trend",     icon: TrendingDown },
    { key: "milestone", label: "Milestone", icon: Package },
  ];
  const visibleKinds = kinds.filter((k) => k.key === "all" || (counts[k.key] || 0) > 0);
  const filtered = filter === "all" ? timeline : timeline.filter((e) => e.kind === filter);

  return (
    <>
      <SectionHeader
        eyebrow="EVIQ Battery Legacy™"
        title="The story of this pack."
      />
      <div className="text-[11px] text-zinc-400 leading-relaxed mb-3 -mt-2">
        Not just what the battery is today — how it got there. Charging habits,
        climate exposure, degradation trends, software updates and significant
        service events, in chronological order.
      </div>
      {/* Kind filter chips */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="passport-legacy-filters">
        {visibleKinds.map((k) => {
          const Icon = k.icon;
          const active = filter === k.key;
          const count = k.key === "all" ? timeline.length : (counts[k.key] || 0);
          return (
            <button
              key={k.key}
              type="button"
              onClick={() => setFilter(k.key)}
              data-testid={`passport-legacy-filter-${k.key}`}
              className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition-colors"
              style={{
                borderColor: active ? "rgba(57,255,20,0.6)" : "rgba(255,255,255,0.10)",
                background: active ? "rgba(57,255,20,0.14)" : "rgba(255,255,255,0.02)",
                color: active ? "#39FF14" : "#a1a1aa",
              }}
            >
              <Icon className="w-2.5 h-2.5" strokeWidth={2.6} />
              {k.label}
              <span className="ml-0.5 text-zinc-500">{count}</span>
            </button>
          );
        })}
      </div>
      <ol className="relative border-l border-white/10 pl-4 mb-6 space-y-3" data-testid="passport-legacy-timeline">
        {filtered.map((e, i) => {
          const Icon = kindIcon[e.kind] || Circle;
          const color = toneColor[e.tone] || "#00E5FF";
          const dt = new Date(e.ts);
          const nice = isNaN(dt) ? "" : dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
          return (
            <li
              key={`${e.ts}-${i}`}
              className="relative"
              data-testid={`passport-legacy-item-${i}`}
            >
              <span
                className="absolute -left-[22px] top-1 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                style={{ background: `${color}18`, border: `1px solid ${color}66` }}
              >
                <Icon className="w-2 h-2" style={{ color }} strokeWidth={3} />
              </span>
              <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                {nice}
              </div>
              <div className="text-[13px] text-white font-semibold leading-tight capitalize">
                {e.title}
              </div>
              {e.detail && (
                <div className="text-[11px] text-zinc-400 leading-snug mt-0.5">
                  {e.detail}
                </div>
              )}
            </li>
          );
        })}
        {filtered.length === 0 && (
          <li className="text-[11px] text-zinc-500 italic pl-1" data-testid="passport-legacy-empty">
            No events match this filter.
          </li>
        )}
      </ol>
    </>
  );
};


// -------------------------------------------------------------------------
// VanityUrlPanel — owner-only. Lets the passport holder claim a memorable
// slug so their URL becomes eviq.pro/p/<slug>. Guarded by signature.
// -------------------------------------------------------------------------
const VanityUrlPanel = ({ passportId, signature, currentSlug, onClaimed }) => {
  const [value, setValue] = React.useState("");
  const [checking, setChecking] = React.useState(false);
  const [availability, setAvailability] = React.useState(null); // {available, reason}
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const publicUrl = React.useMemo(() => {
    if (!currentSlug) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "https://eviq.pro";
    return `${origin}/p/${currentSlug}`;
  }, [currentSlug]);

  // Debounced availability check. The regex mirrors the backend so we
  // give the user immediate feedback without a round-trip on obvious
  // format problems.
  React.useEffect(() => {
    setAvailability(null);
    setErr(null);
    const clean = value.trim().toLowerCase();
    if (!clean) return;
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(clean) || clean.length < 3 || clean.length > 40) {
      setAvailability({ available: false, reason: "invalid_format" });
      return;
    }
    let cancelled = false;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API_BASE}/api/battery/passport/slug/check/${encodeURIComponent(clean)}`);
        const j = await r.json();
        if (!cancelled) setAvailability(j);
      } catch (_e) {
        if (!cancelled) setAvailability({ available: false, reason: "network" });
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 320);
    return () => { cancelled = true; clearTimeout(t); };
  }, [value]);

  const onClaim = async () => {
    const clean = value.trim().toLowerCase();
    if (!clean || !availability?.available) return;
    setSaving(true);
    setErr(null);
    try {
      const r = await fetch(`${API_BASE}/api/battery/passport/${encodeURIComponent(passportId)}/slug`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug: clean, sig: signature }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || "Could not claim slug");
      onClaimed && onClaimed(clean);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setErr(e.message || "Could not claim slug");
    } finally {
      setSaving(false);
    }
  };

  const copyCurrent = async () => {
    if (!publicUrl) return;
    try { await navigator.clipboard.writeText(publicUrl); } catch { /* ignore */ }
  };

  const reasonMsg = availability?.reason === "reserved"
    ? "That word is reserved — try adding your model year."
    : availability?.reason === "taken"
    ? "Already claimed — try another."
    : availability?.reason === "invalid_format"
    ? "3–40 lowercase letters, digits, or hyphens."
    : null;

  return (
    <div
      className="rounded-2xl border p-4 mb-6"
      style={{ borderColor: "rgba(0,229,255,0.28)", background: "rgba(0,229,255,0.05)" }}
      data-testid="passport-vanity-panel"
    >
      <div className="flex items-center gap-2 mb-2">
        <Circle className="w-3 h-3" style={{ color: "#00E5FF" }} strokeWidth={2.6} />
        <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "#00E5FF" }}>
          Custom URL · owner only
        </div>
      </div>

      {currentSlug ? (
        <>
          <div className="text-[13px] text-zinc-200 leading-snug mb-2">
            Your passport lives at
          </div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <code
              className="font-mono text-[12px] text-white px-2.5 py-1.5 rounded border border-white/10 bg-black/40"
              data-testid="passport-vanity-current-url"
            >
              {publicUrl}
            </code>
            <button
              type="button"
              onClick={copyCurrent}
              className="rounded-full border border-white/15 px-3 py-1 text-[11px] text-white hover:bg-white/[0.06]"
              data-testid="passport-vanity-copy"
            >
              Copy
            </button>
          </div>
          <div className="text-[11px] text-zinc-500 leading-relaxed">
            A vanity slug can only be claimed once per passport — pick one you love.
          </div>
        </>
      ) : (
        <>
          <div className="text-[13px] text-zinc-200 leading-snug mb-3">
            Claim a memorable link like <span className="font-mono text-white">eviq.pro/p/silverbolt-2023</span> — makes it way easier to share.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center rounded-md border border-white/15 bg-black/40 pl-2.5 flex-1 min-w-[220px]">
              <span className="font-mono text-[11px] text-zinc-500 select-none">/p/</span>
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value.toLowerCase())}
                placeholder="silverbolt-2023"
                maxLength={40}
                className="bg-transparent flex-1 py-1.5 px-1 text-[13px] text-white font-mono outline-none"
                data-testid="passport-vanity-input"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
              />
              {value && (
                <span
                  className="font-mono text-[10px] mr-2"
                  style={{
                    color: availability?.available ? "#39FF14"
                      : availability ? "#FF6B57"
                      : "#7FD1FF",
                  }}
                >
                  {checking ? "…" : availability?.available ? "✓ available" : availability ? "✗ taken" : ""}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onClaim}
              disabled={!availability?.available || saving}
              className="rounded-full brand-gradient text-black px-4 py-2 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="passport-vanity-claim"
            >
              {saving ? "Claiming…" : saved ? "Claimed ✓" : "Claim"}
            </button>
          </div>
          {reasonMsg && !availability?.available && value && (
            <div className="text-[11px] text-[#FFB020] font-mono mt-2" data-testid="passport-vanity-hint">
              {reasonMsg}
            </div>
          )}
          {err && (
            <div className="text-[11px] text-[#FF6B57] font-mono mt-2">{err}</div>
          )}
          <div className="text-[11px] text-zinc-500 leading-relaxed mt-2">
            Lowercase letters, digits, and hyphens (we'll auto-lowercase). One claim per passport, forever.
          </div>
        </>
      )}
    </div>
  );
};
